// client/src/components/pages/Geofencing/zoneEditor/ZoneEditor.tsx
// The zone editor's map workspace (ST-00239): the map (ZoneEditorMap) plus its
// floating chrome — tool bar, always-visible hint bar, place / coordinate search,
// basemap and layer toggles, the live area / perimeter readout, the keyboard
// shortcuts and their "?" card. State lives in the parent (GeofenceForm) via
// zoneEditorReducer, so the side panel and the map read the same shape.
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, FormEvent, ReactNode } from 'react';
import type { Map as MaplibreMap } from 'maplibre-gl';
import {
  Circle as CircleIcon,
  Focus,
  Keyboard,
  Layers,
  Magnet,
  MousePointer2,
  Pentagon,
  Redo2,
  Search,
  Square,
  Undo2,
  X,
} from 'lucide-react';
import { BasemapToggle as BasemapToggleJs } from '../../../map/LexiStarMap';
import type { ComponentType } from 'react';
import ZoneEditorMap, { framePadding } from './ZoneEditorMap';
import type { ContextZone, EditorOverlay, ZoneEditorMapHandle } from './ZoneEditorMap';
import {
  formatArea,
  formatLength,
  ringAreaM2,
  shapeAreaM2,
  shapeBounds,
  shapePerimeterM,
  type Bounds,
  type LngLat,
} from './zoneGeometry';
import { formatLatLng, parseCoordinateQuery } from './zoneFormat';
import {
  hintFor,
  historyInfo,
  type EditorAction,
  type EditorState,
  type EditorTool,
} from './zoneEditorState';

const BasemapToggle = BasemapToggleJs as unknown as ComponentType<{
  basemap: 'street' | 'satellite';
  onChange: (b: 'street' | 'satellite') => void;
  className?: string;
}>;

export interface EditorSite {
  id: number | string;
  name: string;
  at: LngLat;
}

export interface ZoneEditorProps {
  state: EditorState;
  dispatch: Dispatch<EditorAction>;
  otherZones: ContextZone[];
  overlays: EditorOverlay[];
  overlayBase: string;
  sites: EditorSite[];
  /** The first blocking geometry problem (shown in the hint bar), or null. */
  problem: string | null;
  crossings: LngLat[];
  /** Bounds to frame when the map first loads. */
  initialBounds: Bounds | null;
  /** Keyboard shortcuts off (a dialog above the editor has focus). */
  shortcutsEnabled: boolean;
  snapping: boolean;
  onSnappingChange: (on: boolean) => void;
}

const isEditable = (t: EventTarget | null): boolean => {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    el.isContentEditable ||
    !!el.closest?.('[role="dialog"][data-ze-modal]')
  );
};

/** Coarse pointer (touch screen) — changes wording and handle sizes. */
export function useCoarsePointer(): boolean {
  return useMediaQuery('(pointer: coarse)');
}

/** A CSS media query as React state (false where matchMedia is missing, e.g. tests). */
export function useMediaQuery(query: string): boolean {
  const get = (): boolean =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(query);
    const on = (): void => setMatches(mq.matches);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, [query]);
  return matches;
}

const TOOLS: Array<{ id: EditorTool; label: string; key: string; icon: ReactNode }> = [
  {
    id: 'edit',
    label: 'Edit shape',
    key: 'V',
    icon: <MousePointer2 size={18} aria-hidden="true" />,
  },
  {
    id: 'polygon',
    label: 'Draw polygon',
    key: 'P',
    icon: <Pentagon size={18} aria-hidden="true" />,
  },
  {
    id: 'rectangle',
    label: 'Draw rectangle',
    key: 'R',
    icon: <Square size={18} aria-hidden="true" />,
  },
  {
    id: 'circle',
    label: 'Draw circle',
    key: 'C',
    icon: <CircleIcon size={18} aria-hidden="true" />,
  },
];

const mod =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl';

export const SHORTCUTS: Array<[string, string]> = [
  ['P / R / C', 'Draw a polygon / rectangle / circle'],
  ['V', 'Edit the shape'],
  ['Enter', 'Finish the polygon'],
  ['Esc', 'Cancel drawing, or deselect a corner'],
  ['Backspace', 'Remove the last corner while drawing'],
  ['Delete', 'Remove the selected corner'],
  ['Arrow keys', 'Nudge the selected corner (Shift = 10×)'],
  [`${mod} Z`, 'Undo'],
  [`${mod} Shift Z / ${mod} Y`, 'Redo'],
  ['S', 'Snapping on / off'],
  ['Hold Alt', 'No snapping while held'],
  ['F', 'Zoom to the zone'],
  ['?', 'Show these shortcuts'],
];

function ToolButton({
  label,
  shortcut,
  pressed,
  disabled,
  onClick,
  children,
}: {
  label: string;
  shortcut?: string;
  pressed?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={pressed}
      aria-label={label}
      aria-keyshortcuts={shortcut}
      title={shortcut ? `${label} (${shortcut})` : label}
      className={`flex h-10 w-10 items-center justify-center rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 disabled:cursor-not-allowed disabled:opacity-35 ${
        pressed ? 'bg-teal-500 text-white shadow-inner' : 'text-gray-700 hover:bg-gray-100'
      }`}
    >
      {children}
    </button>
  );
}

const ZoneEditor = forwardRef<ZoneEditorMapHandle, ZoneEditorProps>(function ZoneEditor(
  {
    state,
    dispatch,
    otherZones,
    overlays,
    overlayBase,
    sites,
    problem,
    crossings,
    initialBounds,
    shortcutsEnabled,
    snapping,
    onSnappingChange,
  },
  mapApi
) {
  const touch = useCoarsePointer();
  const narrow = useMediaQuery('(max-width: 639.98px)');
  const [basemap, setBasemap] = useState<'satellite' | 'street'>('satellite');
  const [showOtherZones, setShowOtherZones] = useState(true);
  const [showOverlays, setShowOverlays] = useState(true);
  const [showLayers, setShowLayers] = useState(false);
  const [showKeys, setShowKeys] = useState(false);
  const [query, setQuery] = useState('');
  const [searchMsg, setSearchMsg] = useState<string | null>(null);
  const [searchPin, setSearchPin] = useState<LngLat | null>(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const mapRef = useRef<MaplibreMap | null>(null);
  const innerApi = useRef<ZoneEditorMapHandle | null>(null);

  // Hand the map API to the parent too (zoom-to from the side panel).
  const setApi = useCallback(
    (h: ZoneEditorMapHandle | null) => {
      innerApi.current = h;
      if (typeof mapApi === 'function') mapApi(h);
      else if (mapApi) mapApi.current = h;
    },
    [mapApi]
  );

  const view = state.preview ?? state.shape;
  const history = historyInfo(state);

  // The search pin helps find the place; once a shape is drawn it is clutter.
  const firstShape = useRef(state.shape);
  useEffect(() => {
    if (state.shape !== firstShape.current) setSearchPin(null);
  }, [state.shape]);

  const initialViewState = useMemo(() => {
    if (initialBounds) {
      const [[w, s], [e, n]] = initialBounds;
      return { longitude: (w + e) / 2, latitude: (s + n) / 2, zoom: 15 };
    }
    return { longitude: 24.6727, latitude: -28.4793, zoom: 5 };
    // Only the first value matters (the map is uncontrolled).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onReady = useCallback(
    (map: MaplibreMap) => {
      mapRef.current = map;
      if (initialBounds) {
        const same =
          initialBounds[0][0] === initialBounds[1][0] &&
          initialBounds[0][1] === initialBounds[1][1];
        if (!same)
          map.fitBounds(initialBounds, { padding: framePadding(map), maxZoom: 18, duration: 0 });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const zoomToZone = useCallback(() => {
    const b = view ? shapeBounds(view) : null;
    if (b) innerApi.current?.fitBounds(b);
  }, [view]);

  // ---------- keyboard ----------

  useEffect(() => {
    if (!shortcutsEnabled) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (isEditable(e.target)) return;
      const s = state;
      const k = e.key;
      const handled = (): void => {
        e.preventDefault();
        e.stopPropagation();
      };
      const ctrl = e.ctrlKey || e.metaKey;
      if (ctrl && (k === 'z' || k === 'Z')) {
        dispatch({ type: e.shiftKey ? 'redo' : 'undo' });
        handled();
        return;
      }
      if (ctrl && (k === 'y' || k === 'Y')) {
        dispatch({ type: 'redo' });
        handled();
        return;
      }
      if (ctrl || e.altKey) return;
      if (k === 'Escape') {
        if (showKeys) setShowKeys(false);
        else if (s.draft || s.tool !== 'edit') dispatch({ type: 'cancelDraft' });
        else if (s.selected != null) dispatch({ type: 'select', index: null });
        else return;
        handled();
        return;
      }
      if (k === 'Enter') {
        if (s.draft) {
          dispatch({ type: 'finishDraft' });
          handled();
        }
        return;
      }
      if (k === 'Backspace' || k === 'Delete') {
        if (s.draft?.kind === 'polygon' && k === 'Backspace') dispatch({ type: 'draftUndo' });
        else if (s.selected != null && s.tool === 'edit')
          dispatch({ type: 'deleteVertex', index: s.selected });
        else return;
        handled();
        return;
      }
      if (
        k.startsWith('Arrow') &&
        s.selected != null &&
        s.tool === 'edit' &&
        s.shape?.kind === 'polygon'
      ) {
        const map = mapRef.current;
        const p = s.shape.ring[s.selected];
        if (!map || !p) return;
        const step = e.shiftKey ? 10 : 1;
        const dx = k === 'ArrowLeft' ? -step : k === 'ArrowRight' ? step : 0;
        const dy = k === 'ArrowUp' ? -step : k === 'ArrowDown' ? step : 0;
        const q = map.project(p);
        const ll = map.unproject([q.x + dx, q.y + dy]);
        const ring = s.shape.ring.map((v, i): LngLat => (i === s.selected ? [ll.lng, ll.lat] : v));
        dispatch({
          type: 'commit',
          shape: { kind: 'polygon', ring },
          label: 'Nudge corner',
          mergeKey: `nudge-${s.selected}`,
          at: Date.now(),
        });
        dispatch({ type: 'select', index: s.selected });
        handled();
        return;
      }
      const lower = k.toLowerCase();
      if (lower === 'v') dispatch({ type: 'tool', tool: 'edit' });
      else if (lower === 'p') dispatch({ type: 'tool', tool: 'polygon' });
      else if (lower === 'r') dispatch({ type: 'tool', tool: 'rectangle' });
      else if (lower === 'c') dispatch({ type: 'tool', tool: 'circle' });
      else if (lower === 's') onSnappingChange(!snapping);
      else if (lower === 'f') zoomToZone();
      else if (k === '?') setShowKeys((v) => !v);
      else return;
      handled();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [state, dispatch, shortcutsEnabled, showKeys, snapping, onSnappingChange, zoomToZone]);

  // ---------- search ----------

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2 || parseCoordinateQuery(query)) return [];
    const zones = otherZones
      .filter((z) => z.name.toLowerCase().includes(q))
      .slice(0, 6)
      .map((z) => ({ key: `z${z.id}`, label: z.name, kind: 'Zone', bounds: shapeBounds(z.shape) }));
    const siteHits = sites
      .filter((s) => s.name.toLowerCase().includes(q))
      .slice(0, 6)
      .map((s) => ({
        key: `s${s.id}`,
        label: s.name,
        kind: 'Site',
        bounds: [s.at, s.at] as Bounds,
      }));
    return [...zones, ...siteHits];
  }, [query, otherZones, sites]);

  const goTo = useCallback((b: Bounds | null, pin: LngLat | null) => {
    if (!b) return;
    innerApi.current?.fitBounds(b, 18);
    setSearchPin(pin);
    setSuggestOpen(false);
  }, []);

  const onSearch = (e: FormEvent): void => {
    e.preventDefault();
    const p = parseCoordinateQuery(query);
    if (p) {
      innerApi.current?.flyTo(p, 17);
      setSearchPin(p);
      setSearchMsg(`Showing ${formatLatLng(p)}`);
      window.setTimeout(() => setSearchMsg((m) => (m && m.startsWith('Showing') ? null : m)), 4000);
      setSuggestOpen(false);
      return;
    }
    const first = suggestions[0];
    if (first) {
      goTo(first.bounds, first.kind === 'Site' ? first.bounds![0] : null);
      setSearchMsg(null);
      return;
    }
    setSearchMsg(
      query.trim()
        ? `No zone or site called "${query.trim()}". Paste a position such as -26.2041, 28.0473.`
        : null
    );
  };

  // ---------- hint + readout ----------

  const hint = hintFor(state, { touch, problem });
  const draft = state.draft;

  const readout = useMemo((): string | null => {
    if (draft?.kind === 'polygon' && draft.points.length) {
      const n = draft.points.length;
      const area = n >= 3 ? ` · ${formatArea(ringAreaM2(draft.points))}` : '';
      return `${n} corner${n === 1 ? '' : 's'}${area}`;
    }
    if (!view || draft) return null;
    const area = formatArea(shapeAreaM2(view));
    if (view.kind === 'circle') return `Radius ${formatLength(view.radiusM)} · ${area}`;
    return `${area} · ${formatLength(shapePerimeterM(view))} around · ${view.ring.length} corners`;
  }, [draft, view]);

  const canFinish = draft?.kind === 'polygon' && draft.points.length >= 3;

  return (
    <div className="absolute inset-0">
      <ZoneEditorMap
        ref={setApi}
        state={state}
        dispatch={dispatch}
        otherZones={otherZones}
        overlays={overlays}
        overlayBase={overlayBase}
        showOverlays={showOverlays}
        showOtherZones={showOtherZones}
        basemap={basemap}
        snapping={snapping}
        touch={touch}
        crossings={crossings}
        invalid={crossings.length > 0}
        initialViewState={initialViewState}
        searchPin={searchPin}
        onReady={onReady}
      />

      {/* Search (top left) */}
      <div className="absolute left-3 right-[118px] top-3 z-10 sm:right-auto sm:w-80">
        <form onSubmit={onSearch} role="search" className="relative">
          <label htmlFor="ze-search" className="sr-only">
            Find a place: a zone, a site, or a latitude, longitude
          </label>
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-gray-400"
            aria-hidden="true"
          />
          <input
            id="ze-search"
            type="search"
            value={query}
            autoComplete="off"
            onChange={(e) => {
              setQuery(e.target.value);
              setSearchMsg(null);
              setSuggestOpen(true);
            }}
            onFocus={() => setSuggestOpen(true)}
            onBlur={() => window.setTimeout(() => setSuggestOpen(false), 150)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setQuery('');
                setSearchMsg(null);
                setSearchPin(null);
                (e.target as HTMLInputElement).blur();
              }
            }}
            placeholder="Zone, site or -26.2041, 28.0473"
            aria-describedby={searchMsg ? 'ze-search-msg' : undefined}
            className="h-10 w-full rounded-lg border border-gray-200 bg-surface/95 pl-9 pr-3 text-sm text-gray-900 shadow-lg backdrop-blur placeholder:text-gray-400 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
          {suggestOpen && suggestions.length > 0 && (
            <ul
              role="listbox"
              aria-label="Matching zones and sites"
              className="absolute left-0 right-0 top-11 overflow-hidden rounded-lg border border-gray-200 bg-surface shadow-xl"
            >
              {suggestions.map((s) => (
                <li key={s.key} role="option" aria-selected={false}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setQuery(s.label);
                      goTo(s.bounds, s.kind === 'Site' ? s.bounds![0] : null);
                    }}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100"
                  >
                    <span className="truncate">{s.label}</span>
                    <span className="shrink-0 text-xs text-gray-400">{s.kind}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </form>
        {searchMsg && (
          <p
            id="ze-search-msg"
            role="status"
            className="ml-[3.75rem] mt-1 rounded-md bg-surface/95 px-2 py-1 text-xs text-gray-600 shadow backdrop-blur"
          >
            {searchMsg}
          </p>
        )}
      </div>

      {/* Basemap + layers (top right) */}
      <div className="absolute right-3 top-3 z-10 flex flex-col items-end gap-2">
        <div className="flex items-center gap-2">
          <BasemapToggle basemap={basemap} onChange={setBasemap} className="hidden sm:flex" />
          <button
            type="button"
            onClick={() => setShowLayers((v) => !v)}
            aria-expanded={showLayers}
            aria-controls="ze-layers"
            aria-label="Map layers"
            title="Map layers"
            className="flex h-10 items-center gap-1.5 rounded-lg bg-surface/95 px-3 text-xs font-medium text-gray-700 shadow-lg backdrop-blur hover:bg-gray-100"
          >
            <Layers size={16} aria-hidden="true" />
            <span className="hidden md:inline">Layers</span>
          </button>
        </div>
        {showLayers && (
          <div
            id="ze-layers"
            className="w-56 rounded-lg border border-gray-200 bg-surface/95 p-3 text-sm shadow-xl backdrop-blur"
          >
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">Map</p>
            <BasemapToggle basemap={basemap} onChange={setBasemap} className="mb-3 sm:hidden" />
            <label className="flex cursor-pointer items-center gap-2 py-1 text-gray-700">
              <input
                type="checkbox"
                checked={showOtherZones}
                onChange={(e) => setShowOtherZones(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              Other zones ({otherZones.length})
            </label>
            <label
              className={`flex items-center gap-2 py-1 ${overlays.length ? 'cursor-pointer text-gray-700' : 'text-gray-400'}`}
            >
              <input
                type="checkbox"
                checked={showOverlays && overlays.length > 0}
                disabled={!overlays.length}
                onChange={(e) => setShowOverlays(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              Drone photos ({overlays.length})
            </label>
          </div>
        )}
      </div>

      {/* Tools (left) */}
      <div
        role="toolbar"
        aria-label="Zone drawing tools"
        aria-orientation={narrow ? 'horizontal' : 'vertical'}
        className="absolute left-3 top-16 z-10 flex flex-row gap-1 rounded-lg border border-gray-200 bg-surface/95 p-1 shadow-lg backdrop-blur sm:flex-col"
      >
        {TOOLS.map((t) => (
          <ToolButton
            key={t.id}
            label={t.label}
            shortcut={t.key}
            pressed={state.tool === t.id}
            disabled={t.id === 'edit' && !state.shape}
            onClick={() => dispatch({ type: 'tool', tool: t.id })}
          >
            {t.icon}
          </ToolButton>
        ))}
        <span
          className="mx-0.5 my-1 w-px bg-gray-200 sm:mx-1 sm:my-0.5 sm:h-px sm:w-auto"
          aria-hidden="true"
        />
        <ToolButton
          label={history.undoLabel ? `Undo: ${history.undoLabel}` : 'Undo'}
          shortcut={`${mod}+Z`}
          disabled={!history.canUndo}
          onClick={() => dispatch({ type: 'undo' })}
        >
          <Undo2 size={18} aria-hidden="true" />
        </ToolButton>
        <ToolButton
          label={history.redoLabel ? `Redo: ${history.redoLabel}` : 'Redo'}
          shortcut={`${mod}+Shift+Z`}
          disabled={!history.canRedo}
          onClick={() => dispatch({ type: 'redo' })}
        >
          <Redo2 size={18} aria-hidden="true" />
        </ToolButton>
        <span
          className="mx-0.5 my-1 w-px bg-gray-200 sm:mx-1 sm:my-0.5 sm:h-px sm:w-auto"
          aria-hidden="true"
        />
        <ToolButton
          label={snapping ? 'Snapping to other zones is on' : 'Snapping to other zones is off'}
          shortcut="S"
          pressed={snapping}
          onClick={() => onSnappingChange(!snapping)}
        >
          <Magnet size={18} aria-hidden="true" />
        </ToolButton>
        <ToolButton label="Zoom to the zone" shortcut="F" disabled={!view} onClick={zoomToZone}>
          <Focus size={18} aria-hidden="true" />
        </ToolButton>
        {!touch && (
          <ToolButton
            label="Keyboard shortcuts"
            shortcut="?"
            pressed={showKeys}
            onClick={() => setShowKeys((v) => !v)}
          >
            <Keyboard size={18} aria-hidden="true" />
          </ToolButton>
        )}
      </div>

      {showKeys && (
        <div
          role="region"
          aria-label="Keyboard shortcuts"
          className="absolute left-16 top-16 z-20 w-[min(22rem,calc(100%-5rem))] rounded-lg border border-gray-200 bg-surface p-3 text-sm shadow-xl"
        >
          <div className="mb-2 flex items-center justify-between">
            <p className="font-semibold text-gray-900">Keyboard shortcuts</p>
            <button
              type="button"
              onClick={() => setShowKeys(false)}
              aria-label="Close keyboard shortcuts"
              className="rounded p-1 text-gray-500 hover:bg-gray-100"
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
            {SHORTCUTS.map(([keys, what]) => (
              <div key={keys} className="contents">
                <dt>
                  <kbd className="whitespace-nowrap rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 font-mono text-xs text-gray-700">
                    {keys}
                  </kbd>
                </dt>
                <dd className="text-gray-600">{what}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-2 text-xs text-gray-500">
            Mouse: right-click a corner to delete it. Touch: press and hold a corner.
          </p>
        </div>
      )}

      {/* Readout + hint (bottom) */}
      <div className="pointer-events-none absolute bottom-3 left-3 right-14 z-10 flex flex-col items-start gap-2 sm:right-16">
        {readout && (
          <div
            className="pointer-events-auto rounded-md bg-[#0f172a]/85 px-2.5 py-1 text-xs font-semibold tabular-nums text-white shadow"
            aria-live="off"
            data-testid="zone-readout"
          >
            {readout}
          </div>
        )}
        <div
          role="status"
          aria-live="polite"
          data-testid="zone-hint"
          className={`pointer-events-auto flex w-full max-w-3xl flex-wrap items-center gap-x-3 gap-y-2 rounded-lg px-3 py-2 text-sm shadow-lg ${
            hint.tone === 'warn' ? 'bg-[#b91c1c] text-white' : 'bg-[#0f172a]/90 text-white'
          }`}
        >
          <span className="min-w-0 basis-full sm:basis-auto sm:flex-1">{hint.text}</span>
          {draft && (
            <span className="flex shrink-0 items-center gap-2">
              {canFinish && (
                <button
                  type="button"
                  onClick={() => dispatch({ type: 'finishDraft' })}
                  className="rounded-md bg-[#0d9488] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#14b8a6]"
                >
                  Finish
                </button>
              )}
              {touch && draft.kind === 'polygon' && draft.points.length > 0 && (
                <button
                  type="button"
                  onClick={() => dispatch({ type: 'draftUndo' })}
                  className="rounded-md bg-white/15 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/25"
                >
                  Undo corner
                </button>
              )}
              <button
                type="button"
                onClick={() => dispatch({ type: 'cancelDraft' })}
                className="rounded-md bg-white/15 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/25"
              >
                Cancel
              </button>
            </span>
          )}
          {!draft && state.tool !== 'edit' && state.shape && (
            <button
              type="button"
              onClick={() => dispatch({ type: 'tool', tool: 'edit' })}
              className="shrink-0 rounded-md bg-white/15 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/25"
            >
              Keep current shape
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

export default ZoneEditor;
